// Character Stats Panel - WoW-style character information window

export interface CharacterStats {
  // Primary stats
  strength: number;
  agility: number;
  stamina: number;
  intellect: number;
  spirit: number;

  // Secondary stats
  attackPower: number;
  spellPower: number;
  critChance: number;
  hasteRating: number;

  // Defense
  armor: number;
  defense: number;
  dodgeChance: number;
  parryChance: number;
  blockChance: number;

  // Resources
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;

  // Combat
  mainHandDamage: { min: number; max: number };
  attackSpeed: number;
  dps: number;
}

export class StatsPanel {
  private container: HTMLDivElement;
  private isOpen: boolean = false;
  private stats: CharacterStats;

  constructor() {
    this.stats = this.getDefaultStats();
    this.container = this.createContainer();
    document.getElementById('game-container')?.appendChild(this.container);

    // Toggle with 'C' key
    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'KeyC') {
        this.toggle();
      }
    });
  }

  private getDefaultStats(): CharacterStats {
    return {
      strength: 15,
      agility: 12,
      stamina: 18,
      intellect: 10,
      spirit: 8,
      attackPower: 45,
      spellPower: 20,
      critChance: 5,
      hasteRating: 0,
      armor: 125,
      defense: 5,
      dodgeChance: 3,
      parryChance: 0,
      blockChance: 0,
      health: 100,
      maxHealth: 100,
      mana: 100,
      maxMana: 100,
      mainHandDamage: { min: 12, max: 18 },
      attackSpeed: 2.0,
      dps: 7.5,
    };
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'stats-panel';
    container.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 320px;
      background: linear-gradient(180deg, rgba(30,30,50,0.95) 0%, rgba(20,20,35,0.98) 100%);
      border: 2px solid #4a4a6a;
      border-radius: 8px;
      padding: 15px;
      display: none;
      z-index: 200;
      font-family: 'Segoe UI', Arial, sans-serif;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5);
      pointer-events: auto;
    `;

    this.updateContent(container);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '✕';
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      color: #888;
      font-size: 18px;
      cursor: pointer;
    `;
    closeBtn.addEventListener('click', () => this.close());
    container.appendChild(closeBtn);

    return container;
  }

  private updateContent(container: HTMLDivElement): void {
    const stats = this.stats;

    container.innerHTML = `
      <div style="border-bottom: 1px solid #4a4a6a; padding-bottom: 10px; margin-bottom: 15px;">
        <h2 style="color: #ffd700; margin: 0; font-size: 18px;">Character Stats</h2>
        <div style="color: #888; font-size: 11px;">Press C to toggle</div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
        <!-- Primary Stats -->
        <div>
          <div style="color: #ffd700; font-size: 12px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #3a3a5a; padding-bottom: 4px;">
            Attributes
          </div>
          ${this.statRow('Strength', stats.strength, '#ff9966')}
          ${this.statRow('Agility', stats.agility, '#99ff66')}
          ${this.statRow('Stamina', stats.stamina, '#ff6666')}
          ${this.statRow('Intellect', stats.intellect, '#6699ff')}
          ${this.statRow('Spirit', stats.spirit, '#66ffff')}
        </div>

        <!-- Combat Stats -->
        <div>
          <div style="color: #ffd700; font-size: 12px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #3a3a5a; padding-bottom: 4px;">
            Combat
          </div>
          ${this.statRow('Attack Power', stats.attackPower, '#ffcc00')}
          ${this.statRow('Spell Power', stats.spellPower, '#9966ff')}
          ${this.statRow('Crit Chance', `${stats.critChance.toFixed(1)}%`, '#ff6666')}
          ${this.statRow('Haste', stats.hasteRating, '#66ff99')}
          ${this.statRow('DPS', stats.dps.toFixed(1), '#ffffff')}
        </div>
      </div>

      <!-- Defense Stats -->
      <div style="margin-top: 15px;">
        <div style="color: #ffd700; font-size: 12px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #3a3a5a; padding-bottom: 4px;">
          Defense
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          ${this.statRow('Armor', stats.armor, '#aaaaaa')}
          ${this.statRow('Defense', stats.defense, '#aaaaaa')}
          ${this.statRow('Dodge', `${stats.dodgeChance.toFixed(1)}%`, '#66ff66')}
          ${this.statRow('Parry', `${stats.parryChance.toFixed(1)}%`, '#66ff66')}
        </div>
      </div>

      <!-- Weapon Stats -->
      <div style="margin-top: 15px;">
        <div style="color: #ffd700; font-size: 12px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #3a3a5a; padding-bottom: 4px;">
          Weapon
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          ${this.statRow('Damage', `${stats.mainHandDamage.min}-${stats.mainHandDamage.max}`, '#ffffff')}
          ${this.statRow('Speed', stats.attackSpeed.toFixed(1), '#ffffff')}
        </div>
      </div>

      <!-- Resources -->
      <div style="margin-top: 15px;">
        <div style="color: #ffd700; font-size: 12px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #3a3a5a; padding-bottom: 4px;">
          Resources
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          ${this.statRow('Health', `${stats.health}/${stats.maxHealth}`, '#44ff44')}
          ${this.statRow('Mana', `${stats.mana}/${stats.maxMana}`, '#4488ff')}
        </div>
      </div>
    `;
  }

  private statRow(label: string, value: number | string, color: string): string {
    return `
      <div style="display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 4px;">
        <span style="color: #aaa;">${label}</span>
        <span style="color: ${color}; font-weight: bold;">${value}</span>
      </div>
    `;
  }

  public updateStats(stats: Partial<CharacterStats>): void {
    this.stats = { ...this.stats, ...stats };
    this.updateContent(this.container);
  }

  public setHealth(health: number, maxHealth: number): void {
    this.stats.health = health;
    this.stats.maxHealth = maxHealth;
    if (this.isOpen) {
      this.updateContent(this.container);
    }
  }

  public setMana(mana: number, maxMana: number): void {
    this.stats.mana = mana;
    this.stats.maxMana = maxMana;
    if (this.isOpen) {
      this.updateContent(this.container);
    }
  }

  public open(): void {
    this.isOpen = true;
    this.updateContent(this.container);
    this.container.style.display = 'block';
  }

  public close(): void {
    this.isOpen = false;
    this.container.style.display = 'none';
  }

  public toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public getStats(): CharacterStats {
    return { ...this.stats };
  }

  public destroy(): void {
    this.container.remove();
  }
}
